import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { resetPassword, useSession, validateResetToken } from '@portal/platform/session';
import { AppLink } from '../app-link';
import { fill } from '../i18n/shell-texts';
import { ShellLocaleProvider } from '../i18n/shell-locale';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Alert, AuthCard } from './auth-card';
import { PasswordForm } from './password-form';

/** How long the success message stays before moving on (Angular's 2 s). */
export const REDIRECT_DELAY_MS = 2000;

/**
 * Set a new password from an emailed link. The link is validated first; the page then speaks
 * the language of the user it belongs to, as Angular's did.
 */
export function ResetPasswordPage({ token }: { token: string }) {
  const { platform } = useSession();
  // `platform.client` is the transport, not an input to the answer.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const validation = useQuery({
    queryKey: ['reset-password-token', token],
    queryFn: () => validateResetToken(platform.client, token),
    meta: { scope: 'independent' },
    retry: false,
    staleTime: Infinity,
  });
  return (
    <ShellLocaleProvider language={validation.data?.user?.language}>
      <ResetPasswordContent
        token={token}
        pending={validation.isPending}
        valid={validation.data?.valid === true}
        problem={validation.isError ? 'INVALID_TOKEN' : validation.data?.error}
        name={validation.data?.user?.name ?? ''}
      />
    </ShellLocaleProvider>
  );
}

function ResetPasswordContent({
  token,
  pending,
  valid,
  problem,
  name,
}: {
  token: string;
  pending: boolean;
  valid: boolean;
  problem: string | undefined;
  name: string;
}) {
  const t = useShellTexts().resetPassword;
  const { platform } = useSession();
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) {
      return;
    }
    const timer = setTimeout(
      () => void router.navigate({ href: '/login?passwordReset=true' }),
      REDIRECT_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [done, router]);

  async function submit({ next }: { next: string }) {
    setSubmitting(true);
    setFailed(false);
    try {
      await resetPassword(platform.client, token, next);
      setDone(true);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (pending) {
    return (
      <AuthCard title={t.title}>
        <p role="status" className="text-ink-muted">
          {t.validating}
        </p>
      </AuthCard>
    );
  }

  if (!valid) {
    const message =
      problem === 'TOKEN_EXPIRED'
        ? t.tokenExpired
        : problem === 'TOKEN_ALREADY_USED'
          ? t.tokenAlreadyUsed
          : t.invalidLink;
    return (
      <AuthCard title={t.invalidToken}>
        <Alert tone="error">{message}</Alert>
        <p className="mb-0">
          <AppLink href="/login" className="text-sm text-brand hover:text-brand-dark">
            {t.goToLogin}
          </AppLink>
        </p>
      </AuthCard>
    );
  }

  if (done) {
    return (
      <AuthCard title={t.resetComplete}>
        <Alert tone="success">{t.redirectingToLogin}</Alert>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t.resetHeading}>
      {failed ? <Alert tone="error">{t.invalidLink}</Alert> : null}
      <PasswordForm
        askCurrent={false}
        submitLabel={t.resetButton}
        submittingLabel={t.resetting}
        submitting={submitting}
        onSubmit={submit}
      >
        <p className="mb-2 text-ink">{fill(t.welcome, { name })}</p>
        <p className="mb-4 text-ink-muted">{t.createPassword}</p>
      </PasswordForm>
    </AuthCard>
  );
}
